import * as React from 'react';
import classnames from 'classnames';
import isNil from 'lodash/isNil';

import PageSection from 'components/page/page-section/PageSection';
import SortableTable from 'components/sortable-table/SortableTable';

import { getCcmColumnsByState } from './utils/ccm-campaign-offers-table-columns';

import './ccm-campaign-offers-table.scss';

import type {
  MessengerUnreadChatsMap,
  ResponseTime,
  SortableTableSort,
  Subcampaign
} from 'types';
import type {
  CcmOfferContextMenuActions,
  CcmOfferResult,
  CcmOfferRow,
  CcmOfferTab
} from '../types';

type Props = {
  campaignId: number,
  subcampaignId: number | null,
  data: Array<CcmOfferResult>,
  activeTab: CcmOfferTab,
  responseTimeByInfluencer: {
    [x: number]: ResponseTime
  },
  unreadChatsMap: MessengerUnreadChatsMap,
  showSpinner: boolean,
  onRowClicked: (subcampaignId: number) => void,
  onRemoveFromRevoked: (subcampaign: Subcampaign | null | undefined) => void
};

const CcmCampaignOffersTable: React.FC<Props> = ({
  campaignId,
  subcampaignId,
  data,
  activeTab,
  responseTimeByInfluencer,
  unreadChatsMap,
  showSpinner,
  onRowClicked,
  onRemoveFromRevoked
}): JSX.Element => {
  const [sortingOptions, setSortingOptions] = React.useState({
    sortingKey: 'creator',
    ascending: true
  });

  const getActions = (): CcmOfferContextMenuActions => ({
    onRemoveFromRevoked
  });

  const getRows = (): Array<CcmOfferRow> => (
    data.map((item: CcmOfferResult) => ({
      id: item.creatorProfile.id,
      campaignId,
      subcampaign: item.subcampaign,
      subcampaignId: item.subcampaignId,
      offerState: item.offerState,
      creatorProfile: item.creatorProfile,
      actions: getActions(),
      activeTab,
      isSelected: activeTab !== 'pool' && item.subcampaignId === subcampaignId,
      responseTime: responseTimeByInfluencer[item.creatorProfile.id] ?? null,
      hasUnreadMessages: Boolean(unreadChatsMap[item.conversationExternalId ?? 0]) || false,
      outfits: item.offer?.deliverablesOverview?.zalandoOutfit ?? []
    }))
  );

  const handleRowClicked = (
    row: CcmOfferRow
  ): void => {
    if (isNil(row.subcampaignId)) {
      return;
    }

    onRowClicked(row.subcampaignId);
  };

  const handleSortChange = (
    options: SortableTableSort
  ): void => {
    setSortingOptions(options);
  };

  const tBodyClasses = classnames('ccm-campaign-offers-table__tbody', {
    'ccm-campaign-offers-table__tbody--accepted': activeTab === 'accepted'
  });

  return (
    <div className="ccm-campaign-offers-table">
      <PageSection
        style="volume"
        padding="none"
        margin="none"
      >
        <div className="ccm-campaign-offers-table__body">
          <SortableTable
            maxVirtualPageSize={50}
            layout="dense"
            customClass="ccm-campaign-offers-table__table"
            tbodyClass={tBodyClasses}
            data={getRows()}
            showSpinner={showSpinner}
            defaultSortingKey={sortingOptions.sortingKey}
            defaultSortingDirection={sortingOptions.ascending ? 'ascending' : 'descending'}
            columns={getCcmColumnsByState(data, activeTab)}
            onRowClicked={activeTab !== 'pool' ? handleRowClicked : undefined}
            onSortChanged={handleSortChange}
          />
        </div>
      </PageSection>
    </div>
  );
};

export default CcmCampaignOffersTable;
